import { useEffect, useRef, useState } from "react";

import { MAX_ROW_HEIGHT, MIN_ROW_HEIGHT } from "@corca/tiptap";

import { findNearestColumnEdge, findNearestRowEdge, getRowPos } from "./utils";

import type { Editor } from "@tiptap/core";
import type { Node } from "@tiptap/pm/model";
import type { RefObject } from "react";

type DragState = {
  rowIndex: number;
  startY: number;
  startHeight: number;
  initialEdgeY: number;
  currentDelta: number;
};

type RowResizeState = {
  guideY: number;
};

export function useRowResize(
  containerRef: RefObject<HTMLDivElement | null>,
  editor: Editor,
  node: Node,
  getPos: () => number | undefined,
): RowResizeState | null {
  const dragRef = useRef<DragState | null>(null);
  const [resizeState, setResizeState] = useState<RowResizeState | null>(null);
  const nodeRef = useRef(node);
  nodeRef.current = node;

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    function stopDrag() {
      document.removeEventListener("mousemove", handleDocMouseMove);
      document.removeEventListener("mouseup", handleDocMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    }

    function handleDocMouseMove(e: MouseEvent) {
      const drag = dragRef.current;

      if (!drag) {
        return;
      }

      e.preventDefault();

      const delta = e.clientY - drag.startY;
      const newHeight = drag.startHeight + delta;
      const clampedHeight = Math.min(MAX_ROW_HEIGHT, Math.max(MIN_ROW_HEIGHT, newHeight));
      const clampedDelta = clampedHeight - drag.startHeight;

      drag.currentDelta = clampedDelta;

      setResizeState({ guideY: drag.initialEdgeY + clampedDelta });
    }

    function handleDocMouseUp() {
      const drag = dragRef.current;

      stopDrag();
      setResizeState(null);

      dragRef.current = null;

      if (!drag || Math.abs(drag.currentDelta) < 1) {
        return;
      }

      const tablePos = getPos();

      if (tablePos === undefined) {
        return;
      }

      const newHeight = Math.round(
        Math.min(MAX_ROW_HEIGHT, Math.max(MIN_ROW_HEIGHT, drag.startHeight + drag.currentDelta)),
      );
      const rowPos = getRowPos(nodeRef.current, tablePos, drag.rowIndex);

      editor.commands.setRowHeight(newHeight, rowPos);
    }

    function handleMouseDown(e: MouseEvent) {
      if (!editor.isEditable || !container || e.button !== 0) {
        return;
      }

      // Column resize takes priority over row resize
      if (findNearestColumnEdge(container, e.clientX)) {
        return;
      }

      const edge = findNearestRowEdge(container, e.clientY);

      if (!edge) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      dragRef.current = {
        rowIndex: edge.rowIndex,
        startY: e.clientY,
        startHeight: edge.height,
        initialEdgeY: edge.bottomEdgeY,
        currentDelta: 0,
      };

      setResizeState({ guideY: edge.bottomEdgeY });

      document.body.style.cursor = "row-resize";
      document.body.style.userSelect = "none";

      document.addEventListener("mousemove", handleDocMouseMove);
      document.addEventListener("mouseup", handleDocMouseUp);
    }

    function handleDoubleClick(e: MouseEvent) {
      if (!editor.isEditable || !container) {
        return;
      }

      const tablePos = getPos();

      if (tablePos === undefined) {
        return;
      }

      const columnEdge = findNearestColumnEdge(container, e.clientX);

      if (columnEdge) {
        return;
      }

      const rowEdge = findNearestRowEdge(container, e.clientY);

      if (!rowEdge) {
        return;
      }

      const rowPos = getRowPos(nodeRef.current, tablePos, rowEdge.rowIndex);
      editor.commands.setRowHeight(null, rowPos);
    }

    container.addEventListener("mousedown", handleMouseDown);
    container.addEventListener("dblclick", handleDoubleClick);

    return () => {
      container.removeEventListener("mousedown", handleMouseDown);
      container.removeEventListener("dblclick", handleDoubleClick);
      stopDrag();
    };
  }, [containerRef, editor, getPos]);

  return resizeState;
}
